<template>
    <div class="channel-overall-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="!tableMode && !pieChartMode ? 'selected' : ''"
                        :onClick="
                            () => {
                                tableMode = false;
                                pieChartMode = false;
                            }
                        "
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="!tableMode && pieChartMode ? 'selected' : ''"
                        :onClick="
                            () => {
                                tableMode = false;
                                pieChartMode = true;
                            }
                        "
                        v-tippy="{ content: $t('report.pieView') }"
                    >
                        <md-icon>donut_small</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div v-else class="chart-wrapper">
            <PieChartV2
                :title="title"
                :legends="legends.map((x) => x.legend)"
                :series="getSeriesForPie()"
                v-if="pieChartMode"
                :channelType="channelType"
            />
            <StackedAreaChart
                :title="title"
                :x-axis-data="getXAxisData()"
                :legends="legends.map((x) => x.legend)"
                :colors="colors"
                :series="getSeries()"
                v-else
            />
        </div>

        <div class="stats" v-if="!pieChartMode">
            <StatCard
                :key="key"
                :title="getChannelNameById(key)"
                :value="stats.channelChatCounts[key]"
                v-for="key of Object.keys(stats.channelChatCounts)"
                unit="chats"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";

import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import PieChartV2 from "../../Pages/Chatbot/pages/reports/charts/PieChartV2.vue";

export default Vue.extend({
    name: "ChannelOverallChart",
    components: { PieChartV2, TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        data: {
            type: Object,
            required: true,
        },
        channelType: {
            type: String,
            default: "whatsapp",
        },
        title: {
            type: String,
            default: "Kanallara Göre Chat Dağılımı",
        },
    },
    data() {
        const colorList = [
            "#80FFA5",
            "#00DDFF",
            "#37A2FF",
            "#FF0087",
            "#FFBF00",
            "#EC3131",
            "#C070FF",
            "#FFA500",
            "#A993FF",
            "#FF6B6B",
        ];
        return {
            colorList,
            colors: [],
            legends: [],
            stats: this.getStats([]),
            tableMode: false,
            pieChartMode: false,
            distinctChannels: [],
        };
    },
    methods: {
        initialize() {
            const { buckets, timeSeriesType } = this.data;
            const channels = _.get(buckets, "0.data", []) || [];

            const distinctChannels = channels.map((channel, i) => {
                channel.legend = `${i + 1}-${channel.name}`;
                return channel;
            });
            this.colors = distinctChannels.map((x, i) => this.colorList[i % this.colorList.length]);
            this.legends = distinctChannels;
            this.distinctChannels = distinctChannels;
            this.stats = this.getStats(distinctChannels);
        },
        getCols() {
            return [
                {
                    title: this.$t("message.date"),
                    dataIndex: "date",
                    key: "date",
                },
                ...this.distinctChannels.map((channel) => ({
                    title: channel.name,
                    dataIndex: channel.id,
                    key: channel.id,
                })),
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.data;
            const rows = buckets.map((bucket) => {
                const row = {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                };
                this.distinctChannels.forEach((channel) => {
                    row[channel.id] = bucket.data.find((x) => x.id === channel.id)?.totalChatCount || 0;
                });

                return row;
            });
            return rows;
        },
        getStats(distinctChannels) {
            const { buckets } = this.data;

            let channelChatCounts = buckets.reduce(
                (acc, bucket) => {
                    distinctChannels.forEach((channel) => {
                        acc[channel.id] = acc[channel.id] || 0;
                        acc[channel.id] += bucket.data.find((x) => x.id === channel.id)?.totalChatCount || 0;
                    });
                    return acc;
                },
                {
                    ...distinctChannels.reduce((acc, channel) => {
                        acc[channel.id] = 0;
                        return acc;
                    }, {}),
                }
            );

            return {
                channelChatCounts,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            return this.legends.map((channel, i) => {
                const data = buckets.map((bucket) => {
                    const value = bucket.data.find((x) => x.id === channel.id)?.totalChatCount;
                    return value || 0;
                });
                return {
                    name: channel.legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: this.colors[i % this.colors.length],
                            },
                            {
                                offset: 1,
                                color: "rgb(255, 255, 255)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
            });
        },
        sumChannelTypeCount(type) {
            const result = this.data.buckets.reduce((acc, bucket) => {
                acc += bucket.data.find((x) => x.type === type)?.totalChatCount || 0;
                return acc;
            }, 0);
            console.log(`sumChannelTypeCount Type: ${type}`, result); // Debugging line
            return result;
        },
        getSeriesForPie() {
            return [
                {
                    name: this.$t("message.chatCount"),
                    type: "pie",
                    selectedMode: "single",
                    radius: [0, "30%"],
                    label: {
                        position: "inner",
                        fontSize: 14,
                    },
                    labelLine: {
                        show: false,
                    },
                    data:
                        this.channelType === "whatsapp"
                            ? [
                                  { value: this.sumChannelTypeCount("whatsapp-qr"), name: "Whatsapp V1" },
                                  { value: this.sumChannelTypeCount("whatsapp-v2"), name: "Whatsapp V2" },
                                  { value: this.sumChannelTypeCount("whatsapp-unipile"), name: "Whatsapp V3" },
                                  {
                                      value: this.sumChannelTypeCount("whatsapp-business-api") + this.sumChannelTypeCount("whatsapp-business-api-supsis"),
                                      name: "WA Business",
                                      selected: true,
                                  },
                              ]
                            : this.channelType === "instagram"
                            ? [{ value: this.sumChannelTypeCount("instagram"), name: "Instagram" },{ value: this.sumChannelTypeCount("instagram-v2"), name: "Instagram V2" }]
                            : this.channelType === "messenger"
                            ? [{ value: this.sumChannelTypeCount("messenger"), name: "Messenger" }]
                            : this.channelType === "trendyol"
                            ? [{ value: this.sumChannelTypeCount("trendyol"), name: "Trendyol" }]
                            : this.channelType === "meta-leads"
                            ? [{ value: this.sumChannelTypeCount("meta-leads"), name: "Meta Leads" }]
                            : this.channelType === "linkedin"
                            ? [
                                  {
                                      value: this.sumChannelTypeCount("linkedin-unipile"),
                                      name: "Linkedin",
                                  },
                              ]
                            : this.channelType === "telegram"
                            ? [
                                  {
                                      value: this.sumChannelTypeCount("linkedin-unipile"),
                                      name: "Telegram",
                                  },
                              ]
                            : [],
                },
                {
                    name: "Chat Counts",
                    type: "pie",
                    radius: ["45%", "60%"],
                    labelLine: {
                        length: 30,
                    },
                    label: {
                        formatter: "{a|{a}}{abg|}\n{hr|}\n  {b|{b}：}{c}  {per|{d}%}  ",
                        backgroundColor: "#F6F8FC",
                        borderColor: "#8C8D8E",
                        borderWidth: 1,
                        borderRadius: 4,
                        rich: {
                            a: {
                                color: "#6E7079",
                                lineHeight: 22,
                                align: "center",
                            },
                            hr: {
                                borderColor: "#8C8D8E",
                                width: "100%",
                                borderWidth: 1,
                                height: 0,
                            },
                            b: {
                                color: "#4C5058",
                                fontSize: 14,
                                fontWeight: "bold",
                                lineHeight: 33,
                            },
                            per: {
                                color: "#fff",
                                backgroundColor: "#4C5058",
                                padding: [3, 4],
                                borderRadius: 4,
                            },
                        },
                    },
                    data: [
                        ...this.distinctChannels.map((channel) => ({
                            value: this.data.buckets.reduce((acc, bucket) => {
                                acc += bucket.data.find((x) => x.id === channel.id)?.totalChatCount || 0;
                                return acc;
                            }, 0),
                            name: channel.name,
                        })),
                    ],
                },
            ];
        },
        getChannelNameById(id) {
            return this.distinctChannels.find((x) => x.id === id)?.name || "Unknown Channel";
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;
            const data = buckets.map((bucket) => {
                const row = {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                };
                this.distinctChannels.forEach((channel) => {
                    const channelName = this.getChannelNameById(channel.id);
                    console.log(`Channel ID: ${channel.id}, Channel Name: ${channelName}`); // Debugging line
                    row[channelName] = bucket.data.find((x) => x.id === channel.id)?.totalChatCount || 0;
                });
                return row;
            });
            console.log(data);
            expertToExcel(data, `channel_timeseries.xlsx`, "Sheet 1");
        },
    },
    created() {
        this.initialize();
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                this.initialize();
                this.stats = this.getStats(this.distinctChannels);
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.channel-overall-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .stacked-area-chart {
        height: 500px !important;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1 1 25%;
            max-height: 115px;
        }
    }

    .chart-wrapper {
        width: 100%;
        min-height: 700px;

        .pie-chart-v2 {
            width: 100%;
            min-height: 700px;
        }
    }
}
</style>
